import React from 'react';
import { DataTable, LoadingSpinner, columnTypes, percent, pitchTypeDisplayName, pitchSubTypeColor, date } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import ReportCard from '../../../../components/ReportCard';
import StrikeZone from '../../../../components/StrikeZone';
import { VideoPlayerModal } from '../../../../components/VideoPlayer';
import { usePitchVideoFn } from '../../../../data';
import { damageZonesToSeries } from '../../../../data/dataMapping';

const tooltipFormatter = (translate) => (series, point) => `
    <b>${series.name}</b><br />\
    ${translate('common.date')}: ${date(point.gamedate)}<br/>\
    ${(point.istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `) + point.inning},
    ${point.outs} ${translate('baseball.outs_one')}, ${point.balls}-${point.strikes}
`;

const getColumns = (translate, pitchVideoFn) => [
    { id: 'pitch', title: '', transform: (data) => pitchTypeDisplayName(data, true, true, translate), cellClass: 'fs-6 ps-2', type: columnTypes.NODE },
    { id: 'ZeroZero',
        title: `0-0 ${translate('sections.opponent.player.kzone-dzone')}`,
        transform: (row) => (
            <>
                <StrikeZone
                    enableZoom={false}
                    contourPoints={row.szContour}
                    seriesData={row.pitchesFirst}
                    size="sm"
                    legendposition="below"
                    yopposite
                    showlhb={row.isLhb}
                    showrhb={!row.isLhb}
                    showoffgrid
                    pitcherView={false}
                    pitchVideoFn={pitchVideoFn}
                    videoPlayerModal={VideoPlayerModal}
                    tooltipFormatter={tooltipFormatter(translate)}
                />
                {row.labelsFirst && (
                    <div>
                        {percent(row.labelsFirst.kzone / row.labelsFirst.total)}/{percent(row.labelsFirst.dzone / row.labelsFirst.total)}
                    </div>
                )}
            </>
        ),
        type: columnTypes.NODE },
    { id: 'HitterAhead',
        title: `${translate('sections.opponent.player.hitter-ahead')} ${translate('sections.opponent.player.kzone-dzone')}`,
        transform: (row) => (
            <>
                <StrikeZone
                    enableZoom={false}
                    contourPoints={row.szContour}
                    seriesData={row.pitchesHitterAhead}
                    size="sm"
                    legendposition="below"
                    yopposite
                    showlhb={row.isLhb}
                    showrhb={!row.isLhb}
                    showoffgrid
                    pitcherView={false}
                    pitchVideoFn={pitchVideoFn}
                    videoPlayerModal={VideoPlayerModal}
                    tooltipFormatter={tooltipFormatter(translate)}
                />
                {row.labelsHitterAhead && (
                    <div>
                        {percent(row.labelsHitterAhead.kzone / row.labelsHitterAhead.total)}/
                        {percent(row.labelsHitterAhead.dzone / row.labelsHitterAhead.total)}
                    </div>
                )}
            </>
        ),
        type: columnTypes.NODE },
    { id: 'TwoStrike',
        title: `2K ${translate('sections.opponent.player.kzone-dzone')}`,
        transform: (row) => (
            <>
                <StrikeZone
                    enableZoom={false}
                    contourPoints={row.szContour}
                    seriesData={row.pitchesTwoStrikes}
                    size="sm"
                    legendposition="below"
                    yopposite
                    showlhb={row.isLhb}
                    showrhb={!row.isLhb}
                    showoffgrid
                    pitcherView={false}
                    pitchVideoFn={pitchVideoFn}
                    videoPlayerModal={VideoPlayerModal}
                    tooltipFormatter={tooltipFormatter(translate)}
                />
                {row.labelsTwoStrikes && (
                    <div>
                        {percent(row.labelsTwoStrikes.kzone / row.labelsTwoStrikes.total)}/{percent(row.labelsTwoStrikes.dzone / row.labelsTwoStrikes.total)}
                    </div>
                )}
            </>
        ),
        type: columnTypes.NODE },
];

const getSeriesData = (pitches) => pitches.map((p) => ({
    fxtype: p.fxtype,
    fxsubtype: p.fxsubtype,
    x: p.platex * -1,
    y: p.platez,
    gameid: p.gameid,
    eventseq: p.eventseq,
    pitchseq: p.pitchseq,
    isinstrikezone: p.isinstrikezone,
    pstrike: p.pstrike,
    gamedate: p.gamedate,
    istop: p.istop,
    inning: p.inning,
    outs: p.outs,
    balls: p.balls,
    strikes: p.strikes,
    marker: { symbol: p.stk ? 'circle' : 'square', radius: 4 },
}));

const getSeries = (type, data, translate) => [{
    data,
    name: pitchTypeDisplayName(type, true, true, translate),
    color: pitchSubTypeColor(type),
}];

const generateDamageZoneChartLabel = (series, ellipseDef) => {
    if (!ellipseDef || !series) return null;
    if (ellipseDef.length === 0 || series.length === 0 || series[0].data.length === 0) return null;
    const ellipse = ellipseDef[0];
    const pitches = series[0].data;
    let kzone = 0;
    let dzone = 0;
    pitches.forEach((pitch) => {
        kzone += pitch.pstrike;
        const d1 = Math.sqrt((Math.abs((pitch.x * -1) - ellipse.Focus1X) ** 2) + (Math.abs(pitch.y - ellipse.Focus1Z) ** 2));
        const d2 = Math.sqrt((Math.abs((pitch.x * -1) - ellipse.Focus2X) ** 2) + (Math.abs(pitch.y - ellipse.Focus2Z) ** 2));
        if ((d1 + d2) < ellipse.Radius) dzone += 1;
    });

    return { kzone, dzone, total: pitches.length };
};

const getRows = (isLhb, isLhp, repertoire, pitches, szContours, damageZonesHeatmap, translate) => {
    const rows = [];
    const ellipseDef = damageZonesHeatmap ? damageZonesHeatmap.filter((x) => x.IsLHP === isLhp) : null;
    const damageZonesSeries = { ...damageZonesToSeries(damageZonesHeatmap, isLhp), enableMouseTracking: false };
    const pitchesFirstData = getSeriesData(pitches.filter((p) => p.islhb === isLhb && p.balls === 0 && p.strikes === 0));
    const pitchesTwoStrikesData = getSeriesData(pitches.filter((p) => p.islhb === isLhb && p.strikes === 2 && p.balls !== 3));
    const pitchesHitterAheadData = getSeriesData(pitches.filter((p) => p.islhb === isLhb && p.balls > p.strikes && !(p.balls === 3 && p.strikes === 2)));

    repertoire.filter((type) => type.fxtype !== 1).forEach((type) => {
        const pitchesFirst = getSeries(type, pitchesFirstData.filter((x) => x.fxtype === type.fxtype && x.fxsubtype === type.fxsubtype), translate);
        const labelsFirst = generateDamageZoneChartLabel(pitchesFirst, ellipseDef);
        const pitchesTwoStrikes = getSeries(type, pitchesTwoStrikesData.filter((x) => x.fxtype === type.fxtype && x.fxsubtype === type.fxsubtype), translate);
        const labelsTwoStrikes = generateDamageZoneChartLabel(pitchesTwoStrikes, ellipseDef);
        const pitchesHitterAhead = getSeries(type, pitchesHitterAheadData.filter((x) => x.fxtype === type.fxtype && x.fxsubtype === type.fxsubtype), translate);
        const labelsHitterAhead = generateDamageZoneChartLabel(pitchesHitterAhead, ellipseDef);
        pitchesFirst.push(damageZonesSeries);
        pitchesTwoStrikes.push(damageZonesSeries);
        pitchesHitterAhead.push(damageZonesSeries);
        rows.push({
            fxtype: type.fxtype,
            fxsubtype: type.fxsubtype,
            pitchesFirst,
            labelsFirst,
            pitchesHitterAhead,
            labelsHitterAhead,
            pitchesTwoStrikes,
            labelsTwoStrikes,
            isLhb,
            szContour: isLhb ? szContours.lhb : szContours.rhb,
        });
    });
    return rows;
};

const DamageZones = ({ isLoading, isLhp, isLhb, repertoire, pitches, szContours, damageZonesHeatMap }) => {
    const { t: translate } = useTranslation();
    const pitchVideoFn = usePitchVideoFn();

    return (
        <ReportCard title={translate('pitcher-overview.sections.damage-zones')}>
            {isLoading
                ? <div className="py-5 text-center"> <LoadingSpinner /> </div>
                : (
                    <div className="text-center">
                        <DataTable
                            sortable={false}
                            striped={false}
                            hover={false}
                            rowClass={() => ['border-bottom']}
                            columns={getColumns(translate, pitchVideoFn)}
                            rows={getRows(isLhb, isLhp, repertoire, pitches, szContours, damageZonesHeatMap)}
                        />
                    </div>
                )}
        </ReportCard>
    );
};

export default DamageZones;
